import { clsx, type ClassValue } from 'clsx';
import { translateEnumValue } from './i18n-core';
import { twMerge } from 'tailwind-merge';
import { formatMoney, formattingLocale, MINOR_UNITS_PER_MAJOR, type SessionBranch, type SessionUser } from '@his/shared';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Tenant currency and locale, set once at sign-in and read by every money cell. */
const DISPLAY_DEFAULTS = {
  currency: 'PKR',
  locale: 'en-PK',
  timezone: 'Asia/Karachi',
  country: null as string | null,
};

export const display = { ...DISPLAY_DEFAULTS };

export function resetDisplay(): void {
  Object.assign(display, DISPLAY_DEFAULTS);
}

export function adoptBranchDisplay(branch: SessionBranch, uiLocale: string | null | undefined): void {
  resetDisplay();
  display.currency = branch.currency || DISPLAY_DEFAULTS.currency;
  display.timezone = branch.timezone || DISPLAY_DEFAULTS.timezone;
  display.country = branch.country;
  display.locale = formattingLocale(branch.country, uiLocale);
}

export function activeSessionBranch(user: SessionUser, selectedBranchId: string | null): SessionBranch | null {
  const branches = user.branches ?? [];
  return (
    (selectedBranchId ? branches.find((branch) => branch.id === selectedBranchId) : undefined) ??
    (user.defaultBranchId ? branches.find((branch) => branch.id === user.defaultBranchId) : undefined) ??
    branches[0] ??
    null
  );
}

export function adoptSessionDisplay(user: SessionUser | null, selectedBranchId: string | null): void {
  if (!user || user.isPlatformUser) {
    resetDisplay();
    return;
  }
  const branch = activeSessionBranch(user, selectedBranchId);
  if (!branch) {
    resetDisplay();
    display.timezone = user.timezone || DISPLAY_DEFAULTS.timezone;
    display.locale = formattingLocale(null, user.locale);
    return;
  }
  adoptBranchDisplay(branch, user.locale);
}

/** Amounts arrive as integer minor units; this is the only place they become text. */
export function money(minor: number | null | undefined): string {
  return formatMoney(minor ?? 0, display.currency, display.locale);
}

export function moneyCompact(minor: number | null | undefined): string {
  const major = (minor ?? 0) / MINOR_UNITS_PER_MAJOR;
  try {
    return new Intl.NumberFormat(display.locale, {
      style: 'currency',
      currency: display.currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(major);
  } catch {
    return `${display.currency} ${Math.round(major)}`;
  }
}

export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat(display.locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: display.timezone,
  }).format(new Date(value));
}

export function dateOnly(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat(display.locale, {
    dateStyle: 'medium',
    timeZone: display.timezone,
  }).format(new Date(value));
}

export function timeOnly(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat(display.locale, {
    timeStyle: 'short',
    timeZone: display.timezone,
  }).format(new Date(value));
}

/** "3 min ago" / "in 2 h" — used for waiting times and due doses. */
export function relative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const diffMs = new Date(value).getTime() - Date.now();
  const minutes = Math.round(diffMs / 60_000);
  const formatter = new Intl.RelativeTimeFormat(display.locale, { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  return formatter.format(Math.round(hours / 24), 'day');
}

/** Turns a snake_case enum value into something readable on screen. */
export function humanize(value: string | null | undefined, group?: string): string {
  if (!value) return '—';
  const english = value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  return translateEnumValue(value, english, group) ?? english;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function toIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function todayIso(): string {
  return toIsoDate(new Date());
}

/**
 * A v4 UUID that survives a plain-HTTP address.
 *
 * `crypto.randomUUID` exists only in a secure context, so a hospital reaching
 * the server over http://192.168.x.x — which is most of them on a local
 * network — met "crypto.randomUUID is not a function" the moment anything
 * asked for a key. `getRandomValues` carries no such restriction.
 */
export function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}

/** Idempotency keys for money and dispensing calls. */
export function newIdempotencyKey(): string {
  return uuid();
}

/**
 * Download what is on screen as a spreadsheet (LIMS parity).
 *
 * The owner console had no way out of a grid at all — a renewals list or a
 * facility list could only be read, never handed to an accountant. This builds
 * the file from the rows already fetched, so there is no second query and no
 * risk of exporting something other than what was looked at.
 *
 * A BOM is written first because Excel otherwise reads UTF-8 as Latin-1 and
 * turns every Urdu name and every “—” into mojibake.
 */
export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

/** The file's text, split out from the download so it can be tested. */
export function toCsv<T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string {
  const escape = (value: string | number | null | undefined): string => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  return [
    columns.map((c) => escape(c.header)).join(','),
    ...rows.map((row) => columns.map((c) => escape(c.value(row))).join(',')),
  ].join('\r\n');
}

export function downloadCsv<T>(filename: string, rows: readonly T[], columns: readonly CsvColumn<T>[]): void {
  const blob = new Blob([`﻿${toCsv(rows, columns)}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Copy text, secure context or not.
 *
 * `navigator.clipboard` is undefined over plain http, which is how a hospital
 * on a local network reaches the server. The textarea fallback is the old way
 * and still works everywhere.
 */
export async function copyText(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return legacyCopy(text);
    }
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(area);
  return ok;
}
